import * as React from 'react'

import { Button, Radio } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import createMemo from 'modules/core/hooks/createMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import membershipsCollection from 'modules/teams/store/collections/memberships'
import { membershipIsLead } from 'modules/teams/store/membershipIsLead'
import type { ITeam } from 'modules/teams/store/models/Team'
import { teamMemberships } from 'modules/teams/store/teamMemberships'

interface Props {
  team: ITeam
  onClose: () => void
  selectedGroup?: string
}

const filterEmployees = createMemo((e: Employee[], membershipEmployeeIds: number[]) => {
  return e.filter((employee) => !membershipEmployeeIds.includes(employee.attributes.id))
})

// Convert EmployeeSelectorV2MultiSelectState to backend-compatible format
const selectionToBackendFormat = (selection: EmployeeSelectorV2MultiSelectState) => {
  if (selection.type === 'manual') {
    return {
      type: 'manual',
      ids: selection.ids,
    }
  }
  return {
    type: 'all',
    filters: selection.filters ?? {},
    excluded_ids: selection.excludedIds ?? [],
    include_inactive: selection.includeInactive ?? false,
  }
}

const MembersModal = ({ team, onClose, selectedGroup = '' }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({
      type: 'manual',
      ids: [],
    })
  const memberships = teamMemberships(membershipsCollection, employees, team.id)
  const membershipEmployeeIds = memberships.map((m) => m.attributes.employee_id)
  const [group, setGroup] = React.useState(selectedGroup)
  const sortEmployees = (input: Employee[]) =>
    orderBy(
      input,
      (e) => {
        const userId = e.attributes.access_id
        const user = users.get(userId)
        if (!user) {
          throw Error(`Error getting users with id = ${userId}`)
        }
        return user.fullName()
      },
      ['asc']
    )
  const employeesToShow = filterEmployees(employees.active, membershipEmployeeIds)
  const leaders = memberships.filter((membership) => membershipIsLead(employees, membership))

  const leadersToShow = employees.active.filter(
    (x) => !leaders.some((y) => y.attributes.employee_id === x.attributes.id)
  )

  const getSelectedIds = (): number[] => {
    return employeeSelection.type === 'manual' ? employeeSelection.ids : []
  }

  const onSubmit = async () => {
    const selectionInput = selectionToBackendFormat(employeeSelection)
    const selectedIds = getSelectedIds()

    const { success, value } = await asyncAction(() =>
      membershipsCollection.rpc('bulk_upsert', {
        team_id: team.id,
        employees_ids: group === 'members' ? selectedIds : [],
        lead_employees_ids: group === 'leaders' ? selectedIds : [],
        employee_selection: group === 'members' ? selectionInput : undefined,
        lead_employee_selection: group === 'leaders' ? selectionInput : undefined,
      })
    )

    track(trackingEvents.teams.addTeamDetailsMembersSubmit, {
      is_successful: success,
      error_code: !success && JSON.stringify(value),
      number_of_members: selectedIds.length,
    })

    if (success) {
      onClose()
      membershipsCollection.set(value, { add: true, change: true, remove: false })
    }

    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  const handleClose = () => {
    onClose()
    track(trackingEvents.teams.addTeamDetailsMembersCancel)
  }

  const handleGroupChange = (value: string) => {
    setGroup(value)
    // Reset selection when switching groups
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        title={i18n.t('teams.members_modal.title')}
        description={i18n.t('teams.members_modal.description')}
        illustration="group"
        onClose={handleClose}
        onSubmit={onSubmit}
        footer={<Button submit>{i18n.t('common.submit')}</Button>}
      >
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_members')}
            value={group}
            onChange={handleGroupChange}
            description={i18n.t('teams.members.new_description')}
            option={{
              label: i18n.t('teams.add_team_members'),
              value: 'members',
            }}
          >
            <EmployeeSelectorV2
              multiple
              onChange={setEmployeeSelection}
              forcedFilters={{
                employeeIds: sortEmployees(employeesToShow).map((e) => e.id),
              }}
            />
          </Radio>
        </Radio.Wrap>
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_leaders')}
            value={group}
            onChange={handleGroupChange}
            description={i18n.t('teams.leaders_modal.description')}
            option={{
              label: i18n.t('teams.add_team_leaders'),
              value: 'leaders',
            }}
          >
            <EmployeeSelectorV2
              multiple
              onChange={setEmployeeSelection}
              forcedFilters={{
                employeeIds: sortEmployees(leadersToShow).map((e) => e.id),
              }}
            />
          </Radio>
        </Radio.Wrap>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(MembersModal)
